import React, { useEffect, useState } from "react";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, { Dayjs } from "dayjs";
import 'dayjs/locale/en-au';
import { Box, CircularProgress, Stack, ToggleButton, ToggleButtonGroup } from "@mui/material";
import { useVillageWifiHttp } from "./hooks/useVillageWifiHttp";
import { IssuedKeyDto } from "./dtos/IssuedKeyDto";
import { IssuedKeysStats } from "./IssuedKeysStats";
import { VillageDeviceOccurrenceDto } from "./dtos/VillageDeviceOccurrenceDto";
import { VillageDeviceOccurrenceStats } from "./VillageDeviceOccurrenceStats";
import { VillageDeviceAuthStats } from "./VillageDeviceAuthStats";
import { VillageDeviceAuthDto } from "./dtos/VillageDeviceAuthDto";
import { VillageIssuedKeysStats } from "./VillageIssuedKeysStats";


enum TimePeriod {

    Today
    , Yesterday
    , ThisWeek
    , LastWeek
    , ThisMonth
    , LastMonth
    , Custom
    , ByHour

};

const toggleButtonSx = { textTransform: 'none' };


export const VillageWifiDashboard: React.FC = ( ) => {

    const [ dateFrom, setDateFrom ] = useState< string  >( );
    const [ dateTo, setDateTo ] = useState< string  >( );
    const [ isLoaded, setIsLoaded ] = useState< boolean >( false );
    const [ isLoading, setIsLoading ] = useState< boolean >( false );
    const [ issuedKeys, setIssuedKeys ] = useState< IssuedKeyDto[ ]>( [ ] );
    const [ timePeriod, setTimePeriod ] = useState<TimePeriod>( );
    const [ villageDeviceOccurrences, setVillageDeviceOccurrences ] = useState< VillageDeviceOccurrenceDto[] >( [ ] );
    const [ villageDeviceAuths, setVillageDeviceAuths ] = useState< VillageDeviceAuthDto[] >( [ ] );
    

    const { getIssuedKeys, getVillageDeviceAuths, getVillageDeviceOccurrences } = useVillageWifiHttp( );


    useEffect( ( ) => {

        if( !dateFrom || !dateTo ){
            
            return;

        }

        const fetchData = async( dateFrom: string, dateTo: string ) => {

            setIssuedKeys( [ ] );
            setVillageDeviceOccurrences( [ ] );
            setVillageDeviceAuths( [ ] );

            setIsLoaded( false );
            setIsLoading( true );

            const issuedKeys = await getIssuedKeys( dateFrom, dateTo );

            if( issuedKeys ){
                
                const formattedIssuedKeys: IssuedKeyDto[ ] = [];
                
                for( const issuedKey of issuedKeys ) {
                    
                    formattedIssuedKeys.push({
                        keyCount: issuedKey.keyCount,
                        keyIssuedTo: issuedKey.keyIssuedTo,
                        village: issuedKey.village ? issuedKey.village : 'VillageOps Portal'
                    } as IssuedKeyDto );

                }

                setIssuedKeys( formattedIssuedKeys );

            } 

            const villageDeviceOccurrences = await getVillageDeviceOccurrences( dateFrom, dateTo );
            
            if( villageDeviceOccurrences ){

                setVillageDeviceOccurrences( villageDeviceOccurrences );

            } 

            const villageDeviceAuths = await getVillageDeviceAuths( dateFrom, dateTo );
            
            if( villageDeviceAuths ){

                setVillageDeviceAuths( villageDeviceAuths );

            }

            setIsLoaded( true );
            setIsLoading( false );

        };

        const dateFromIsValid = dayjs( dateFrom.slice( 0, 10 ), 'YYYY-MM-DD', true ).isValid( );
        const dateToIsValid = dayjs( dateTo.slice( 0, 10 ), 'YYYY-MM-DD', true ).isValid( );
        const dateToGteDateFrom = dayjs( dateTo ) >= dayjs( dateFrom );

        if( dateFromIsValid && dateToIsValid && dateToGteDateFrom ) {

            fetchData( dateFrom, dateTo );

        }

    }, [ dateFrom, dateTo ]);


    const handleDateFromChange = ( date: Dayjs | null ) => {

        if( !date ){
            
            return;

        }

        const day = date.date( ) < 10 ? `0${ date.date( ) }` : `${ date.date( ) }`;
        const month = date.month( ) + 1 < 10 ? `0${ date.month( ) + 1 }` : `${ date.month( ) + 1 }`;
        const year = date.year( );
        const formattedDate = `${ year }-${ month }-${ day }`;
        setDateFrom( formattedDate ); 
        
    };


    const handleDateToChange = ( date: Dayjs | null ) => {

        if( !date ){
            
            return;

        }

        const day = date.date( ) < 10 ? `0${ date.date( ) }` : `${ date.date( ) }`;
        const month = date.month( ) + 1 < 10 ? `0${ date.month( ) + 1 }` : `${ date.month( ) + 1 }`;
        const year = date.year( );
        const formattedDate = `${ year }-${ month }-${ day }`;

        setDateTo( formattedDate ); 
        
    };


    const handleTimePeriodChange = ( _: React.MouseEvent<HTMLElement>, newTimePeriod: TimePeriod ) => {
     
        setTimePeriod( newTimePeriod );

        switch( newTimePeriod ){

            case TimePeriod.Today:

                setDateFrom( dayjs().startOf('day').toISOString( ) );
                setDateTo( dayjs().toISOString( ) );
                break;

            case TimePeriod.Yesterday:

                setDateFrom( dayjs( ).startOf('day').subtract( 1, 'day' ).toISOString( ) );
                setDateTo( dayjs( ).startOf('day').subtract( 1, 'day' ).endOf('day').toISOString( ) );
                break;

            case TimePeriod.ThisWeek:

                setDateFrom( dayjs().startOf('week').toISOString( ) );
                setDateTo( dayjs().toISOString( ) );
                break;

            case TimePeriod.LastWeek:

                setDateFrom( dayjs( ).startOf( 'week' ).subtract( 1, 'week' ).toISOString( ) );
                setDateTo( dayjs( ).startOf( 'week' ).subtract( 1, 'week' ).endOf( 'week' ).toISOString( ) );
                break;

            case TimePeriod.ThisMonth:

                setDateFrom( dayjs().startOf("month").toISOString() );
                setDateTo( ( new Date( ) ).toISOString( ) );
                break;

            case TimePeriod.LastMonth:

                setDateFrom( dayjs( ).startOf( 'month' ).subtract( 1, 'month' ).toISOString( ) );
                setDateTo( dayjs( ).startOf( 'month' ).subtract( 1, 'month' ).endOf( 'month' ).toISOString( ) );
                break;

        }

    }


    return(
        
        <Stack direction="column" display="flex" alignItems="center" gap={ 0.5 }>

            <ToggleButtonGroup 
                color="primary"
                value={ timePeriod }
                exclusive
                onChange={ handleTimePeriodChange }
            >

                <ToggleButton value={ TimePeriod.Today } sx={ toggleButtonSx }>Today</ToggleButton>
                <ToggleButton value={ TimePeriod.Yesterday } sx={ toggleButtonSx }>Yesterday</ToggleButton>
                <ToggleButton value={ TimePeriod.ThisWeek } sx={ toggleButtonSx }>This Week</ToggleButton>
                <ToggleButton value={ TimePeriod.LastWeek } sx={ toggleButtonSx }>Last Week</ToggleButton>
                <ToggleButton value={ TimePeriod.ThisMonth } sx={ toggleButtonSx }>This Month</ToggleButton>
                <ToggleButton value={ TimePeriod.LastMonth } sx={ toggleButtonSx }>Last Month</ToggleButton>
                <ToggleButton value={ TimePeriod.Custom } sx={ toggleButtonSx }>Custom</ToggleButton>
                <ToggleButton value={ TimePeriod.ByHour } sx={ toggleButtonSx }>Hourly</ToggleButton>

            </ToggleButtonGroup>

            {
                timePeriod === TimePeriod.Custom
                    &&
                <LocalizationProvider dateAdapter={ AdapterDayjs } adapterLocale="en-au">
                
                    <Stack direction="row" sx={{ marginTop: '10px' }}>

                        <DatePicker 
                            sx={{ marginTop: '5px' }}
                            label="Date From" 
                            value = { dateFrom ? dayjs( dateFrom ) : null }
                            onChange={ handleDateFromChange }
                            maxDate = { dateTo ? dayjs( dateTo ) : undefined }
                        />

                        <div style={{ display: 'flex', alignItems: 'center', marginLeft: '10px', marginRight: '10px', fontWeight: 400, fontSize: 30, color: 'rgb(28, 32, 37)' }}>-</div>

                        <DatePicker 
                            disabled={ !dateFrom } 
                            sx={{ marginTop: '5px' }}
                            label="Date To" 
                            value = { dateTo ? dayjs( dateTo ) : null }
                            onChange={ handleDateToChange }
                            minDate={ dayjs( dateFrom ) }
                        />

                    </Stack>
                
                </LocalizationProvider>

            }

            {

                timePeriod == TimePeriod.ByHour
                    ?
                <VillageIssuedKeysStats />
                    :
                <>
                    <Box 
                    display="flex" 
                    alignItems="center"
                    justifyContent="center"
                >
                
                    {
                        isLoading
                            ?
                        <CircularProgress size={ 80 } sx={{ color: '#e65400', marginTop: '50px' }}/>
                            :
                        <Box sx={{ height: '40px', width: '40px' }}></Box>
                    }

                </Box>

                { isLoaded && <IssuedKeysStats issuedKeys={ issuedKeys } /> }

                { isLoaded && <VillageDeviceOccurrenceStats villageDeviceOccurrences={ villageDeviceOccurrences } /> }

                { isLoaded && <VillageDeviceAuthStats villageDeviceAuths={ villageDeviceAuths } /> }

                </>

            }

            
        

        </Stack>

    );

};